import React, { useState, useMemo } from 'react';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { PALETTE } from './dashDummyData';
import { KPI_ACCENTS, DEEP_ANALYSIS_TABS } from './dashConstants';
import ExportDash from './ExportDash';

const TAB_HEADERS = {
    regionState: ['Region', 'States', 'Top State', 'Quotations', 'Clients', 'QTY','Share', 'Top Collection'],
    catSeg: ['Category', 'Quotations', 'Clients','QTY', 'Share', 'Top Tier', 'Segments', 'Regions'],
    userPerf: ['Representative', 'Quotations', 'Clients', 'QTY','Share', 'Role', 'Top Collection', 'Regions'],
};

function ShareCell({ pct, barColor = 'linear-gradient(90deg,#0a2540,#1a6b5e)' }) {
    return (
        <td className="p-3">
            <div className="flex items-center gap-2">
                <span className="font-bold text-slate-600 w-9 tabular-nums text-[11px]">{pct}%</span>
                <div className="w-20 bg-slate-100 h-1 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: barColor }} />
                </div>
            </div>
        </td>
    );
}

function RegionStateRow({ row, i }) {
    return (
        <tr key={i} className="hover:bg-slate-50/60 transition-colors">
            <td className="p-3 font-bold text-slate-800 flex items-center gap-2">
                {row.region}
            </td>
            <td className="p-3 font-bold" style={{ color: '#0a2540' }}>{row.states}</td>
            <td className="p-3 text-slate-500 font-semibold">{row.topState}</td>
            <td className="p-3 font-black text-slate-900" style={{ fontFamily: '"DM Mono", monospace' }}>{row.quotations.toLocaleString()}</td>
            <td className="p-3 font-black text-slate-900" style={{ fontFamily: '"DM Mono", monospace' }}>{row.totalClients}</td>
            <td className="p-3 font-black text-slate-900" style={{ fontFamily: '"DM Mono", monospace' }}>{row.totalQty?.toLocaleString()}</td>
            <ShareCell pct={row.sharePercentOfQuotation} />
            <td className="p-3 text-slate-500 font-semibold">{row.topCollection}</td>
        </tr>
    );
}

function CatSegRow({ row, i }) {
    return (
        <tr key={i} className="hover:bg-slate-50/60 transition-colors">
            <td className="p-3 font-bold text-slate-800">{row.category}</td>
            <td className="p-3 font-black text-slate-900" style={{ fontFamily: '"DM Mono", monospace' }}>{row.quotations.toLocaleString()}</td>
            <td className="p-3 font-black text-slate-900" style={{ fontFamily: '"DM Mono", monospace' }}>{row.totalClients}</td>
            <td className="p-3 font-black text-slate-900" style={{ fontFamily: '"DM Mono", monospace' }}>{row.totalQty?.toLocaleString()}</td>
            <ShareCell pct={row.sharePercentOfQuotation} />
            <td className="p-3">
                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-slate-100 text-slate-700 uppercase tracking-wide border border-slate-200">
                    {row.topSegment}
                </span>
            </td>
            <td className="p-3 text-slate-500 font-semibold">{row.segments}</td>
            <td className="p-3 font-bold" style={{ color: '#0a2540' }}>{row.regions}</td>
        </tr>
    );
}

function UserPerfRow({ row, i }) {
    return (
        <tr key={i} className="hover:bg-slate-50/60 transition-colors">
            <td className="p-3 font-bold text-slate-800">
                <div className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-[9px] font-black text-slate-400 bg-slate-100 border border-slate-200 rounded-md">
                        {i + 1}
                    </span>
                    {row.name}
                </div>
            </td>
            <td className="p-3 font-black text-slate-900" style={{ fontFamily: '"DM Mono", monospace' }}>{row.quotations.toLocaleString()}</td>
            <td className="p-3 font-black text-slate-900" style={{ fontFamily: '"DM Mono", monospace' }}>{row.totalClients}</td>
            <td className="p-3 font-black text-slate-900" style={{ fontFamily: '"DM Mono", monospace' }}>{row.totalQty?.toLocaleString()}</td>
            <ShareCell pct={row.sharePercentOfQuotation} barColor={PALETTE[i % PALETTE.length]} />
            <td className="p-3 text-slate-500 font-semibold">{row.role}</td>
            <td className="p-3 text-slate-500 font-semibold">{row.topCollection}</td>
            <td className="p-3 font-bold" style={{ color: '#0a2540' }}>{row.regions}</td>
        </tr>
    );
}

export default function DeepAnalysisTable({ analyticsSec1, analyticsSec2, analyticsSec3, filters = {} }) {
    const [currentTab, setCurrentTab] = useState('regionState');

    const dynamicCategories = useMemo(() => {
        const rows = analyticsSec3?.monthCollectionQuotationDistribution;
        if (!rows?.length) return [];

        const uniqueKeys = new Set();
        rows.forEach((row) => {
            Object.keys(row).forEach((key) => {
                if (key !== 'month' && key !== 'totalQuotations') uniqueKeys.add(key);
            });
        });
        return Array.from(uniqueKeys);
    }, [analyticsSec3]);

    const insightCards = analyticsSec1?.insights || [];

    return (
        <section
            className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
            style={{ boxShadow: '0 1px 4px rgba(10,37,64,0.06)' }}
        >
            {/* Header + tab switcher */}
            <div className="px-6 py-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <ViewModuleIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                        <h2 className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Deep Analysis</h2>
                    </div>

                </div>
                <div className="flex flex-wrap gap-0.5 bg-slate-100 p-0.5 rounded-xl">
                    {DEEP_ANALYSIS_TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setCurrentTab(t.id)}
                            className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all ${currentTab === t.id
                                ? 'bg-white text-[#0a2540] shadow-sm'
                                : 'text-slate-400 hover:text-slate-600'
                                }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table body */}
            <div className="p-5 overflow-x-auto min-h-[220px]">

                {/* ── Tabular views ── */}
                {['regionState', 'catSeg', 'userPerf', 'monthCollection'].includes(currentTab) && (
                    <table className="w-full text-left text-xs border-collapse">
                        <thead>
                            <tr className="border-b border-slate-100">
                                {(currentTab === 'monthCollection'
                                    ? ['Month', ...dynamicCategories.map(c => c.charAt(0).toUpperCase() + c.slice(1)), 'Total']
                                    : TAB_HEADERS[currentTab]
                                ).map((h) => (
                                    <th key={h} className="p-3 text-[10px] font-black text-slate-400 uppercase tracking-widest pb-2">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {currentTab === 'regionState' &&
                                (analyticsSec3?.regionStateAnalytics || []).map((row, i) => (
                                    <RegionStateRow key={i} row={row} i={i} />
                                ))
                            }

                            {currentTab === 'catSeg' &&
                                (analyticsSec2?.categorySegmentAnalytics || []).map((row, i) => (
                                    <CatSegRow key={i} row={row} i={i} />
                                ))
                            }

                            {currentTab === 'userPerf' &&
                                (analyticsSec3?.userPerformance || []).map((row, i) => (
                                    <UserPerfRow key={i} row={row} i={i} />
                                ))
                            }

                            {currentTab === 'monthCollection' &&
                                (analyticsSec3?.monthCollectionQuotationDistribution || []).map((row, i) => (
                                    <tr key={i} className="hover:bg-slate-50/60 transition-colors">
                                        <td className="p-3 font-black text-slate-900">{row.month}</td>
                                        {dynamicCategories.map((catKey) => (
                                            <td key={catKey} className="p-3 text-slate-500 font-semibold" style={{ fontFamily: '"DM Mono", monospace' }}>
                                                {(row[catKey] || 0).toLocaleString()}
                                            </td>
                                        ))}
                                        <td className="p-3 font-black" style={{ color: '#0a2540', fontFamily: '"DM Mono", monospace' }}>
                                            {(row.totalQuotations || 0).toLocaleString()}
                                        </td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                )}

                {/* ── Insights / Summary view ── */}
                {currentTab === 'insights' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {insightCards.map((ins, i) => (
                            <div
                                key={i}
                                className="relative bg-white rounded-2xl border border-slate-100 p-4 flex flex-col gap-2 overflow-hidden"
                                style={{
                                    boxShadow: '0 1px 3px rgba(10,37,64,0.05)',
                                    borderLeftColor: PALETTE[i % PALETTE.length],
                                    borderLeftWidth: 3,
                                }}
                            >
                                <div className="flex items-center gap-1.5">
                                    <EmojiEventsIcon sx={{ fontSize: 13, color: PALETTE[i % PALETTE.length] }} />
                                    <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{ins.title}</div>
                                </div>
                                <div
                                    className="text-lg font-black text-slate-800 leading-tight"
                                    style={{ fontFamily: '"DM Mono", monospace' }}
                                >
                                    {ins.value}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}